import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, Button, TextField, Grid, Typography, Card, CardContent, CardHeader, Autocomplete, 
  FormControl, FormGroup, FormControlLabel, Checkbox, Stack, Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { client, MarketCatalogue, Instance, Market, MarketOrderType, MarketSupplier } from '../../app/services/api/orderManagementClient';
import { isOrderTypeDSD } from '../../enums/OrderType';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';

const CatalogueForm = () => {
  const { t } = useTranslation('catalogues');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketCatalogue>({
    customerGroupId: 0,
    instanceName: '',
    companyId: 0,
    name: '',
    message: '',
    marketName: '',
    customerTypeId: 0,
    erpInternalId: undefined,
    orderTypeIds: [],
    supplierIds: [],
  });

  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<MarketOrderType[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<MarketSupplier[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchMarkets = async (companyId: number) => {
    try {
      const result = await client.getMarkets({
        companyId: companyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setMarkets(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchOrderTypes = async (companyId: number, customerTypeId?: number) => {
    try {
      const result = await client.getMarketOrderTypes({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setOrderTypes(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching order types:', error);
    }
  };

  const fetchSuppliers = async (companyId: number, customerTypeId?: number) => {
    try {
      const result = await client.getMarketSuppliers({
        companyId: companyId.toString(),
        customerTypeId: customerTypeId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setSuppliers(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCatalogue = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await client.getMarketCatalogue(id);
      if (result.success && result.data) {
        const catalogue = result.data;
        setFormData({
          ...catalogue,
          orderTypeIds: catalogue.orderTypeIds ?? [],
          supplierIds: catalogue.supplierIds ?? [],
        });
        const instance = instances.find(i => i.id === catalogue.companyId);
        if (instance) {
          setSelectedInstance(instance);
          fetchMarkets(instance.id);
        }
      }
    } catch (error) {
      console.error('Error fetching catalogue:', error);
      setMessage(t('management.loadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    if (instances.length > 0 && isEdit && id) {
      fetchCatalogue();
    }
  }, [instances, isEdit, id]);

  useEffect(() => {
    if (isEdit && formData.customerGroupId && selectedInstance && markets.length > 0) {
      const market = markets.find(m => m.customerTypeId === formData.customerTypeId);
      if (market && !selectedMarket) {
        setSelectedMarket(market);
        fetchOrderTypes(selectedInstance.id, market.customerTypeId);
        fetchSuppliers(selectedInstance.id, market.customerTypeId);
      }
    }
  }, [markets, formData.customerTypeId, selectedInstance, isEdit]);

  useEffect(() => {
    const orderTypeIds = formData.orderTypeIds ?? [];
    if (isEdit && orderTypeIds.length > 0 && orderTypes.length > 0) {
      const orderTypesData = orderTypes.filter(ot => orderTypeIds.includes(ot.orderTypeId));
      if (orderTypesData.length > 0 && selectedOrderTypes.length === 0) {
        setSelectedOrderTypes(orderTypesData);
      }
    }
  }, [orderTypes, formData.orderTypeIds, isEdit]);

  useEffect(() => {
    const supplierIds = formData.supplierIds ?? [];
    if (suppliers.length > 0 && selectedSuppliers.length === 0) {
      if (isEdit && supplierIds.length > 0) {
        const suppliersData = suppliers.filter(s => supplierIds.includes(s.supplierId));
        if (suppliersData.length > 0) {
          setSelectedSuppliers(suppliersData);
        }
      } else if (formData.name && selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId))) {
        const matchingSuppliers = suppliers.filter(supplier => 
          supplier.supplierName === formData.name,
        );
        
        if (matchingSuppliers.length > 0) {
          setSelectedSuppliers(matchingSuppliers);
          setFormData(prev => ({
            ...prev,
            supplierIds: matchingSuppliers.map(s => s.supplierId),
          }));
        }
      }
    }
  }, [suppliers, formData.supplierIds, formData.name, selectedOrderTypes, selectedSuppliers.length, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('management.catalogueNameRequired');
    }

    if (!selectedInstance) {
      newErrors.instance = t('management.instanceRequired');
    }

    if (!selectedMarket) {
      newErrors.market = t('management.marketRequired');
    }

    if (selectedOrderTypes.length === 0) {
      newErrors.orderTypes = t('management.orderTypesRequired');
    }

    const isDsdSelected = selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId));
    if (isDsdSelected && selectedSuppliers.length === 0) {
      newErrors.suppliers = t('management.suppliersRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketCatalogue) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: newValue,
    }));
    
    if (field === 'name' && selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId)) && newValue) {
      const matchingSuppliers = suppliers.filter(supplier => 
        supplier.supplierName === newValue,
      );
      
      if (matchingSuppliers.length > 0) {
        const newSelectedSuppliers = [...selectedSuppliers];
        matchingSuppliers.forEach(supplier => {
          if (!newSelectedSuppliers.some(s => s.supplierId === supplier.supplierId)) {
            newSelectedSuppliers.push(supplier);
          }
        });
        
        setSelectedSuppliers(newSelectedSuppliers);
        setFormData(prev => ({
          ...prev,
          supplierIds: newSelectedSuppliers.map(s => s.supplierId),
        }));
      }
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setSelectedInstance(value);
    setSelectedMarket(null);
    setSelectedOrderTypes([]);
    setSelectedSuppliers([]);
    setMarkets([]);
    setOrderTypes([]);
    setSuppliers([]);
    
    if (value) {
      setFormData(prev => ({
        ...prev,
        companyId: value.id,
        instanceName: value.name,
      }));
      fetchMarkets(value.id);
    } else {
      setFormData(prev => ({
        ...prev,
        companyId: 0,
        instanceName: '',
      }));
    }
    
    if (errors.instance) {
      setErrors(prev => ({ ...prev, instance: '' }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setSelectedMarket(value);
    setSelectedOrderTypes([]);
    setSelectedSuppliers([]);
    setOrderTypes([]);
    setSuppliers([]);
    
    if (value) {
      setFormData(prev => ({
        ...prev,
        customerTypeId: value.customerTypeId,
        marketName: value.name,
      }));
      if (selectedInstance) {
        fetchOrderTypes(selectedInstance.id, value.customerTypeId);
        fetchSuppliers(selectedInstance.id, value.customerTypeId);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        customerTypeId: 0,
        marketName: '',
      }));
    }
    
    if (errors.market) {
      setErrors(prev => ({ ...prev, market: '' }));
    }
  };

  const handleOrderTypeChange = (orderType: MarketOrderType, checked: boolean) => {
    let newSelectedOrderTypes: MarketOrderType[];
    
    if (checked) {
      newSelectedOrderTypes = [...selectedOrderTypes, orderType];
      
      if (isOrderTypeDSD(orderType.orderTypeId) && formData.name) {
        const matchingSuppliers = suppliers.filter(supplier => 
          supplier.supplierName === formData.name,
        );
        
        const newSelectedSuppliers = [...selectedSuppliers];
        matchingSuppliers.forEach(supplier => {
          if (!newSelectedSuppliers.some(s => s.supplierId === supplier.supplierId)) {
            newSelectedSuppliers.push(supplier);
          }
        });
        
        setSelectedSuppliers(newSelectedSuppliers);
        setFormData(prev => ({
          ...prev,
          supplierIds: newSelectedSuppliers.map(s => s.supplierId),
        }));
      }
    } else {
      newSelectedOrderTypes = selectedOrderTypes.filter(ot => ot.orderTypeId !== orderType.orderTypeId);
      
      if (isOrderTypeDSD(orderType.orderTypeId)) {
        setSelectedSuppliers([]);
        setFormData(prev => ({
          ...prev,
          supplierIds: [],
        }));
      }
    }
    
    setSelectedOrderTypes(newSelectedOrderTypes);
    setFormData(prev => ({
      ...prev,
      orderTypeIds: newSelectedOrderTypes.map(ot => ot.orderTypeId),
      message: newSelectedOrderTypes.length === 1 && isOrderTypeDSD(newSelectedOrderTypes[0].orderTypeId) ? '12' : '',
    }));
    
    if (errors.orderTypes) {
      setErrors(prev => ({ ...prev, orderTypes: '' }));
    }
  };

  const handleSupplierChange = (supplier: MarketSupplier, checked: boolean) => {
    let newSelectedSuppliers: MarketSupplier[];
    
    if (checked) {
      newSelectedSuppliers = [...selectedSuppliers, supplier];
    } else {
      newSelectedSuppliers = selectedSuppliers.filter(s => s.supplierId !== supplier.supplierId);
    }
    
    setSelectedSuppliers(newSelectedSuppliers);
    setFormData(prev => ({
      ...prev,
      supplierIds: newSelectedSuppliers.map(s => s.supplierId),
    }));
    
    if (errors.suppliers) {
      setErrors(prev => ({ ...prev, suppliers: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      let result;
      
      if (isEdit) {
        result = await client.updateMarketCatalogue(id!, formData);
        if (result.success) {
          setMessage(t('management.updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          const params = getUrlParams();
          navigate(`/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
        } else {
          setMessage(t('management.updateErrorMessage'));
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        result = await client.createMarketCatalogue(formData);
        if (result.success) {
          setMessage(t('management.createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          const params = getUrlParams();
          navigate(`/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
        } else {
          setMessage(t('management.createErrorMessage'));
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      console.error('Error saving catalogue:', error);
      setMessage(isEdit ? t('management.updateErrorMessage') : t('management.createErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/catalogues${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'CatalogueUpdate' : 'CatalogueCreate', 'ConfigurationAdmin']);
  const isDsdSelected = selectedOrderTypes.some(ot => isOrderTypeDSD(ot.orderTypeId));

  if (isLoading) {
    return (
      <PageArea>
        <Box sx={{ mb: 4 }}>
          <PageTitle>
            {isEdit ? t('management.editCatalogue') : t('management.createCatalogue')}
          </PageTitle>
        </Box>
        <Typography>{t('management.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={{ mb: 4 }}>
        <PageTitle>
          {isEdit ? t('management.editCatalogue') : t('management.createCatalogue')}
        </PageTitle>
      </Box>

      <Grid
        container
        spacing={4}
      >
        {/* Basic Information */}
        <Grid
          item
          xs={12}
        >
          <Card elevation={1}>
            <CardHeader
              title={t('management.basicInformation')}
              sx={{ color: 'text.primary' }}
            />
            <CardContent sx={{ p: 8 }}>
              <Grid
                container
                spacing={6}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextField
                    fullWidth
                    label={t('management.catalogueName')}
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>
                
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextField
                    fullWidth
                    label={t('management.erpInternalId')}
                    value={formData.erpInternalId || ''}
                    onChange={handleInputChange('erpInternalId')}
                    type="number"
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Autocomplete
                    options={instances}
                    getOptionLabel={(option) => option.name}
                    value={selectedInstance}
                    onChange={handleInstanceChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('management.selectInstance')}
                        error={!!errors.instance}
                        helperText={errors.instance}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Autocomplete
                    options={markets}
                    getOptionLabel={(option) => option.name}
                    value={selectedMarket}
                    onChange={handleMarketChange}
                    disabled={!selectedInstance}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('management.selectMarket')}
                        error={!!errors.market}
                        helperText={errors.market}
                        required
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Type Assignment - Only show if market is selected */}
        {selectedMarket && (
          <Grid
            item
            xs={12}
          >
            <Card elevation={1}>
              <CardHeader
                title={t('management.orderTypeAssignment')}
                sx={{ color: 'text.primary' }}
              />
              <CardContent sx={{ p: 8 }}>
                {errors.orderTypes && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                  >
                    {errors.orderTypes}
                  </Alert>
                )}
                
                <FormControl component="fieldset">
                  <FormGroup>
                    {orderTypes.map((orderType) => (
                      <FormControlLabel
                        key={orderType.orderTypeId}
                        control={
                          <Checkbox
                            checked={selectedOrderTypes.some(ot => ot.orderTypeId === orderType.orderTypeId)}
                            onChange={(e) => handleOrderTypeChange(orderType, e.target.checked)}
                          />
                        }
                        label={orderType.name}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        )}        
        {/* Supplier Assignment - Only show if DSD order type is selected */}
        {isDsdSelected && (
          <Grid
            item
            xs={12}
          >
            <Card elevation={1}>
              <CardHeader
                title={t('management.supplierAssignment')}
                sx={{ color: 'text.primary' }}
              />
              <CardContent sx={{ p: 8 }}>
              
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                >
                  {t('management.dsdOrderTypeNote')}
                </Alert>
                
                {errors.suppliers && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                  >
                    {errors.suppliers}
                  </Alert>
                )}
              
                <FormControl component="fieldset">
                  <FormGroup>
                    {suppliers.map((supplier) => (
                      <FormControlLabel
                        key={supplier.supplierId}
                        control={
                          <Checkbox
                            checked={selectedSuppliers.some(s => s.supplierId === supplier.supplierId)}
                            onChange={(e) => handleSupplierChange(supplier, e.target.checked)}
                          />
                        }
                        label={`${supplier.supplierNumber} - ${supplier.supplierName}`}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Actions */}
        <Grid
          item
          xs={12}
        >
          <Stack
            direction="row"
            spacing={3}
            justifyContent="flex-end"
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('management.cancel')}
            </Button>
            {userCanSave && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? t('management.saving') : (isEdit ? t('management.update') : t('management.create'))}
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      <MessagesSnackbar
        open={showMessage}
        message={message}
        severity={isSuccess ? 'success' : 'error'}
        duration={5000}
        onClose={handleCloseMessage}
      />
    </PageArea>
  );
};

export default CatalogueForm;
